/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `cardValidation` for the Caintra Connect frontend.
 * @module utils/payment/cardValidation
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
/**
 * Card number, expiry formatting, and validation helpers shared across payment modals.
 */

/**
 * Detects the card type based on the card number prefix.
 * @param {string} cardNumber - The card number (may contain spaces)
 * @returns {string} Card type: "visa", "mastercard", "amex", "discover", or empty string
 */
export const detectCardType = (cardNumber) => {
  const number = cardNumber.replace(/\s/g, "");
  if (/^4/.test(number)) {
    return "visa";
  }
  if (/^5[1-5]/.test(number)) {
    return "mastercard";
  }
  if (/^3[47]/.test(number)) {
    return "amex";
  }
  if (/^6/.test(number)) {
    return "discover";
  }
  return "";
};

/**
 * Validates an expiry date in MM/YY format.
 * @param {string} expiryDate - Expiry date in MM/YY format
 * @param {Date} [referenceDate=new Date()] - Date used to check expiration
 * @returns {{isValid: boolean, error: string}} Validation result
 */
export const validateExpiryDate = (expiryDate, referenceDate = new Date()) => {
  if (!expiryDate || expiryDate.length !== 5) {
    return { isValid: false, error: "Invalid date format" };
  }

  const [month, year] = expiryDate.split("/");
  const currentYear = referenceDate.getFullYear() % 100;
  const currentMonth = referenceDate.getMonth() + 1;

  const expiryMonth = parseInt(month, 10);
  const expiryYear = parseInt(year, 10);

  if (expiryMonth < 1 || expiryMonth > 12) {
    return { isValid: false, error: "Invalid month" };
  }

  if (
    expiryYear < currentYear ||
    (expiryYear === currentYear && expiryMonth < currentMonth)
  ) {
    return { isValid: false, error: "Card has expired" };
  }

  return { isValid: true, error: "" };
};

/**
 * Formats a card number by adding spaces every 4 digits.
 * @param {string} value - Raw card number input
 * @returns {string} Formatted card number
 */
export const formatCardNumber = (value) => {
  const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
  const matches = v.match(/\d{4,16}/g);
  const match = (matches && matches[0]) || "";
  const parts = [];
  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }
  if (parts.length) {
    return parts.join(" ");
  }
  return v;
};

/**
 * Formats an expiry date input to MM/YY format.
 * @param {string} value - Raw expiry date input
 * @returns {string} Formatted expiry date
 */
export const formatExpiryDate = (value) => {
  const v = value.replace(/[^0-9/]/g, "");
  const digits = v.replace(/\//g, "");

  if (digits.length >= 3) {
    return digits.substring(0, 2) + "/" + digits.substring(2, 4);
  }

  if (digits.length === 2) {
    return digits;
  }

  return digits;
};

/**
 * Validates card form fields before OpenPay tokenization.
 * @param {Object} formData - Payment form data
 * @throws {Error} When any card field is invalid
 */
export const validateCardFormFields = (formData) => {
  const cardNumber = formData.numeroTarjeta.replace(/\s/g, "");
  const expiryParts = formData.fechaDeVencimiento.split("/");

  if (!cardNumber || cardNumber.length < 13 || cardNumber.length > 19) {
    throw new Error("Invalid card number length");
  }

  if (
    !expiryParts[0] ||
    !expiryParts[1] ||
    expiryParts[0].length !== 2 ||
    expiryParts[1].length !== 2
  ) {
    throw new Error("Invalid expiration date format");
  }

  if (
    !formData.codigoDeSeguridad ||
    formData.codigoDeSeguridad.length < 3 ||
    formData.codigoDeSeguridad.length > 4
  ) {
    throw new Error("Invalid CVV length");
  }

  if (!formData.nombreTitular || formData.nombreTitular.trim().length === 0) {
    throw new Error("Cardholder name is required");
  }
};

/**
 * Builds the OpenPay tokenization payload from form data.
 * @param {Object} formData - Payment form data
 * @param {string} deviceSessionId - OpenPay device session id
 * @returns {{ cardData: Object, cardNumber: string }}
 */
export const buildOpenPayCardData = (formData, deviceSessionId) => {
  const cardNumber = formData.numeroTarjeta.replace(/\s/g, "");
  const expiryParts = formData.fechaDeVencimiento.split("/");

  return {
    cardNumber,
    cardData: {
      card_number: cardNumber,
      holder_name: formData.nombreTitular.trim(),
      expiration_year: expiryParts[1],
      expiration_month: expiryParts[0],
      cvv2: formData.codigoDeSeguridad,
      device_session_id: deviceSessionId,
    },
  };
};
